import { useEffect, useState, useRef, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MakeApiCallAsync } from '../../../helpers/ApiHelpers';
import Config from '../../../helpers/Config';

import { useSelector } from 'react-redux';
import { Alert, Button, Card, CardHeader, CardTitle, CardSubtitle, CardBody, Form, FormGroup, FormFeedback, Label, Input } from 'reactstrap'
import NewCustomerCard from './NewCustomerCard';
import { validateEmail } from '../../../helpers/ValidationHelper';
import { getLanguageCodeFromSession, GetLocalizationControlsJsonDataForScreen, replaceLoclizationLabel } from '../../../helpers/CommonHelper';
import GlobalEnums from '../../../helpers/GlobalEnums';
import Seo from '../../components/shared/Seo';
import { useLoginMutation, useLoginExtQuery } from '../../../services/auth';
import LoadingScreen from '../../components/shared/LoadingScreen';
import { useAppDispatch } from '../../../stateManagment/reduxStore';
import rootAction from '../../../stateManagment/actions/rootAction';
import UpdateEmployeeIdLoginInfo from '../../views/login/UpdateEmployeeIdLoginInfo.js';
import { api } from '../../../services/api';
import SSOCustomerCard from './SSOCustomerCard';
import { useGetContentAssetQuery } from '../../../services/content';
import { ToggleContext,useSwitching } from '../../../ToggleProvider';
import { useSwitchSessionMutation } from '../../../services/auth';
import { useLazyGetSiteInfoQuery } from '../../../services/site';
import { showLiveChat } from '../../../helpers/chatWidget';

const SSO_EXT_AUTH_SESSION_KEY = 'ua_sso_extauth_params';
const LOGIN_DEBUG_TELEMETRY_ENDPOINT = 'https://b2b-telemetry.uacorporate.com/postevent';
const LOGIN_DEBUG_BROWSER_SESSION_KEY = 'ua_login_debug_browser_session_id';
const LOGIN_DEBUG_QUEUE_KEY = 'ua_login_debug_queue';
const LOGIN_DEBUG_BATCH_SIZE = 20;
const LOGIN_DEBUG_ALLOWED_HOSTS = [
    'veg.uacorporate.com',
    'adventhealth.uacorporate.com',
    'freseniusmedicalcare.uacorporate.com',
    'stg-cf-veg.uacorporate.com',
    'stg-cf-adventhealth.uacorporate.com',
    'stg-cf-freseniusmedicalcare.uacorporate.com'
];
let loginDebugFlushInFlight = false;

const readLoginDebugQueue = () => {
    try {
        const raw = sessionStorage.getItem(LOGIN_DEBUG_QUEUE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const writeLoginDebugQueue = (events) => {
    try {
        const bounded = Array.isArray(events) ? events.slice(-500) : [];
        sessionStorage.setItem(LOGIN_DEBUG_QUEUE_KEY, JSON.stringify(bounded));
    } catch (error) {
        // best effort only
    }
};

const getLoginDebugBrowserSessionId = () => {
    try {
        const existing = sessionStorage.getItem(LOGIN_DEBUG_BROWSER_SESSION_KEY);
        if (existing) return existing;
        const created = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        sessionStorage.setItem(LOGIN_DEBUG_BROWSER_SESSION_KEY, created);
        return created;
    } catch (error) {
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
};

const isLoginDebugEnabled = () => {
    try {
        return LOGIN_DEBUG_ALLOWED_HOSTS.includes(window.location.hostname);
    } catch (_) {
        return false;
    }
};

const postLoginDebugBatch = async (events) => {
    if (!events?.length || !isLoginDebugEnabled()) return false;
    const body = JSON.stringify({ events });
    try {
        if (navigator?.sendBeacon) {
            const blob = new Blob([body], { type: 'application/json' });
            const sent = navigator.sendBeacon(LOGIN_DEBUG_TELEMETRY_ENDPOINT, blob);
            if (sent) return true;
        }
    } catch (error) {
        // fallback below
    }

    try {
        await fetch(LOGIN_DEBUG_TELEMETRY_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
            credentials: 'omit',
            cache: 'no-store'
        });
        return true;
    } catch (error) {
        return false;
    }
};

const flushLoginDebugQueueAsync = async () => {
    if (loginDebugFlushInFlight) return;
    loginDebugFlushInFlight = true;
    try {
        let queue = readLoginDebugQueue();
        while (queue.length > 0) {
            const batch = queue.slice(0, LOGIN_DEBUG_BATCH_SIZE);
            const posted = await postLoginDebugBatch(batch);
            if (!posted) break;
            queue = queue.slice(batch.length);
            writeLoginDebugQueue(queue);
        }
    } finally {
        loginDebugFlushInFlight = false;
    }
};

const enqueueLoginDebugEvent = (eventPayload) => {
    if (!isLoginDebugEnabled()) return;
    const queue = readLoginDebugQueue();
    queue.push(eventPayload);
    writeLoginDebugQueue(queue);

    if (typeof window?.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => {
            void flushLoginDebugQueueAsync();
        }, { timeout: 1000 });
    } else {
        setTimeout(() => {
            void flushLoginDebugQueueAsync();
        }, 0);
    }
};

const Login = () => {
    const flowIdRef = useRef(window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const flowStartedAtRef = useRef(Date.now());
    const pollAttemptRef = useRef(0);
    const pollStartedLoggedRef = useRef(false);
    const prevIsExtLoadingRef = useRef(false);
    const terminalEventLoggedRef = useRef(false);

    const logLoginDebug = (step, payload = {}) => {
        const urlSearchParams = new URLSearchParams(window.location.search || '');
        const loginSessionIdFromUrl = urlSearchParams.get('LoginSessionID');
        let persistedSsoParams = null;
        try {
            const persistedRaw = sessionStorage.getItem(SSO_EXT_AUTH_SESSION_KEY);
            persistedSsoParams = persistedRaw ? JSON.parse(persistedRaw) : null;
        } catch (error) {
            persistedSsoParams = null;
        }
        const browserSessionId = getLoginDebugBrowserSessionId();
        const resolvedLoginSessionId = payload?.resolvedLoginSessionId
            ?? payload?.externalLoginSessionID
            ?? payload?.extLoginSessionId
            ?? payload?.loginSessionId
            ?? loginSessionIdFromUrl
            ?? persistedSsoParams?.LoginSessionID
            ?? null;
        const resolvedSP = payload?.resolvedSP
            ?? payload?.externalSP
            ?? payload?.extSp
            ?? payload?.SP
            ?? urlSearchParams.get('SP')
            ?? persistedSsoParams?.SP
            ?? null;
        const customerNumber = payload?.customerNumber
            ?? payload?.CustomerNo
            ?? payload?.customerNo
            ?? payload?.customer_no
            ?? payload?.loginExtData?.CustomerNo
            ?? payload?.loginExtData?.customerNo
            ?? payload?.loginExtData?.customer_no;
        const pollAttempt = payload?.pollAttempt ?? pollAttemptRef.current;

        const eventPayload = {
            component: 'Login',
            step,
            timestamp: new Date().toISOString(),
            flowId: flowIdRef.current,
            elapsedMsFromFlowStart: Date.now() - flowStartedAtRef.current,
            browserSessionId,
            loginSessionIdFromUrl,
            resolvedLoginSessionId,
            resolvedSP,
            baseUrl: window.location.origin,
            pathname: window.location.pathname,
            search: window.location.search,
            href: window.location.href,
            userAgent: navigator.userAgent,
            pollAttempt,
            customerNumber: customerNumber || null,
            payload
        };

        enqueueLoginDebugEvent(eventPayload);

        console.log(`[Login][SSO-DEBUG] ${step}`, payload);
    };

    const isBackendUnreachableError = (error) => {
        const status = error?.status ?? error?.originalStatus ?? error?.data?.statusCode;
        const rawMessage = error?.error ?? error?.message ?? error?.statusText ?? '';
        const message = String(rawMessage).toLowerCase();
        return status === 'FETCH_ERROR'
            || status === 0
            || message.includes('failed to fetch')
            || message.includes('networkerror')
            || message.includes('network request failed')
            || message.includes('load failed')
            || message.includes('offline');
    };

    const logBackendUnreachable = (source, error, payload = {}) => {
        if (!isBackendUnreachableError(error)) return;
        logLoginDebug('Backend unreachable detected', {
            source,
            isOnline: navigator.onLine,
            error,
            ...payload
        });
    };

    const logTerminalEventOnce = (outcome, payload = {}) => {
        if (terminalEventLoggedRef.current) return;
        terminalEventLoggedRef.current = true;
        logLoginDebug('login_flow_terminal', {
            outcome,
            ...payload
        });
    };

    logLoginDebug('Entered Login component', {
        renderedAt: new Date().toISOString()
    });
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [showError, setShowError] = useState(false);
    const [showUpdateEmployeeIdLoginInfo, setShowUpdateEmployeeIdLoginInfo] = useState(false);
    const [LocalizationLabelsArray, setLocalizationLabelsArray] = useState([]);
    const [Email, setEmail] = useState('');
    const [Password, setPassword] = useState('');
    const [flashMsg, setFlashMsg] = useState('');
    const [ShowPassword, setShowPassword] = useState(true);
    const SignupOption = useSelector(state => state.commonReducer.selfRegister);
    const SsoCompany = useSelector(state => state.commonReducer.siteName);
    const SsoOption = useSelector(state => state.commonReducer.enableSSO);
    const blockDirectLogin = useSelector(state => state.commonReducer.blockDirectLogin);
    const SamlLoginEndpoint = useSelector(state => state.commonReducer.samlLoginEndpoint);
    const SamlLogoutEndpoint = useSelector(state => state.commonReducer.samlLogoutEndpoint);
    const LoginSystem = useSelector(state => state.commonReducer.loginSystem);
    const [formValid, setFormValid] = useState(null);
    const [login, { isFetching, isLoading }] = useLoginMutation();
    const loginInFlightRef = useRef(false);
    const loginExtDataRef = useRef();
    const { user, rememberMe, flashMessage } = useSelector(state => state.userReducer);
    const [formErrors, setFormErrors] = useState({});
    const [shippingAddress, setShippingAddress] = useState({ address1: "", address2: "", city: "", stateCode: "", countryCode: "US", postalCode: "", allotmentAddressLabel: "", allotmentAddressId: 0});
    const [phone, setPhone] = useState('');
    const [firstName, setFirstName] = useState('');
    const [isExtLoginError, setIsExtLoginError] = useState(false);
    const [skipPolling, setSkipPolling] = useState(false);
    /** When true, extauth polling for this LoginSessionID is done (success or handoff). Never derive skip from stale loginExtDataRef — that skipped all polls on a new SP/LoginSessionID return. */
    const [ssoExtAuthComplete, setSsoExtAuthComplete] = useState(false);
    const [showErrorForEmpIdLogin, setShowErrorForEmpIdLogin] = useState(false);
    const [authenticationDone, setAuthenticationDone] = useState(null);
    const accountLoginIntroText = useSelector(state => state.commonReducer.cachedContentAssets?.['account-login-intro-text']);
    const ssoButtonText = useSelector(state => state.commonReducer.cachedContentAssets?.['sso-login-button-text']);
    const [isSsoEmployeeLoginInfoLoaded, setIsSsoEmployeeLoginInfoLoaded] = useState(false);
    const { isToggled, setIsToggled } = useContext(ToggleContext);
    const [switchSession, { isSwitching }] = useSwitchSessionMutation();
    const [getSiteInfo, { isLoadingSiteInfo }] = useLazyGetSiteInfoQuery();
    const { showSwitching, hideSwitching } = useSwitching();
    const { setIntentToSwitch } = useContext(ToggleContext);

    const [searchParams] = useSearchParams();
    const qsParsed = {
        ExtLoginError: searchParams.get('ExtLoginError'),
        SP: searchParams.get('SP'),
        LoginSessionID: searchParams.get('LoginSessionID'),
        showUaLogin: searchParams.get('show') === '1'
    };

    useEffect(() => {
        const onOnline = () => {
            void flushLoginDebugQueueAsync();
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                void flushLoginDebugQueueAsync();
            }
        };

        window.addEventListener('online', onOnline);
        document.addEventListener('visibilitychange', onVisibilityChange);
        void flushLoginDebugQueueAsync();

        return () => {
            window.removeEventListener('online', onOnline);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);
    /**
     * PII / LogRocket: capture SP + LoginSessionID once, then strip LoginSessionID from the address bar (replaceState via navigate).
     * Compatible with SFCC-2884 SSO polling (2s interval, no retry counter) — keep ext* values here so polling still runs after URL cleanup.
     */
    const ssoExtAuthParamsRef = useRef(null);
    const hasObservedExtAuthRequestRef = useRef(false);
    const hasLoggedInitialSnapshotRef = useRef(false);
    if (qsParsed.LoginSessionID) {
        const nextSsoParams = { SP: qsParsed.SP, LoginSessionID: qsParsed.LoginSessionID };
        logLoginDebug('Query string contains LoginSessionID; persisting SSO params', {
            qsParsed,
            nextSsoParams,
            existingSsoExtAuthParamsRef: ssoExtAuthParamsRef.current
        });
        ssoExtAuthParamsRef.current = nextSsoParams;
        try {
            sessionStorage.setItem(SSO_EXT_AUTH_SESSION_KEY, JSON.stringify(nextSsoParams));
            logLoginDebug('Persisted SSO params to sessionStorage', {
                key: SSO_EXT_AUTH_SESSION_KEY,
                persistedValue: nextSsoParams
            });
        } catch (error) {
            console.log(error);
            logLoginDebug('Failed persisting SSO params to sessionStorage', {
                key: SSO_EXT_AUTH_SESSION_KEY,
                error
            });
        }
    } else if (!ssoExtAuthParamsRef.current) {
        try {
            const persistedSsoParams = sessionStorage.getItem(SSO_EXT_AUTH_SESSION_KEY);
            logLoginDebug('No LoginSessionID in query; attempting restore from sessionStorage', {
                key: SSO_EXT_AUTH_SESSION_KEY,
                persistedSsoParams
            });
            if (persistedSsoParams) {
                const parsedSsoParams = JSON.parse(persistedSsoParams);
                if (parsedSsoParams?.LoginSessionID) {
                    ssoExtAuthParamsRef.current = {
                        SP: parsedSsoParams.SP,
                        LoginSessionID: parsedSsoParams.LoginSessionID
                    };
                    logLoginDebug('Restored SSO params from sessionStorage', {
                        restoredSsoParams: ssoExtAuthParamsRef.current
                    });
                }
            }
        } catch (error) {
            console.log(error);
            logLoginDebug('Failed restoring SSO params from sessionStorage', {
                key: SSO_EXT_AUTH_SESSION_KEY,
                error
            });
        }
    }
    const extSp = ssoExtAuthParamsRef.current?.SP ?? qsParsed.SP;
    const extLoginSessionId = ssoExtAuthParamsRef.current?.LoginSessionID ?? qsParsed.LoginSessionID;
    // PunchOut/SSO handoff keeps /login usable; only block empty direct visits.
    const showPunchOutOnlyMessage = blockDirectLogin === true && !extLoginSessionId;
    // Do not reference loginExtData here — it is declared below by useLoginExtQuery (TDZ: "Cannot access before initialization").
    // Require at least one extauth poll per LoginSessionID: only stop after ssoExtAuthComplete for this session (not stale ref UserID).
    const skipExtPolling = skipPolling || !extLoginSessionId || ssoExtAuthComplete;

    useEffect(() => {
        pollAttemptRef.current = 0;
        pollStartedLoggedRef.current = false;
        prevIsExtLoadingRef.current = false;
        terminalEventLoggedRef.current = false;
        loginExtDataRef.current = undefined;
        setSsoExtAuthComplete(false);
        setSkipPolling(false);
    }, [extLoginSessionId]);

    const prevSkipExtPollingRef = useRef(skipExtPolling);
    if (prevSkipExtPollingRef.current !== skipExtPolling) {
        logLoginDebug('skipExtPolling changed', {
            previous: prevSkipExtPollingRef.current,
            current: skipExtPolling,
            reasons: {
                skipPolling,
                extLoginSessionIdMissing: !extLoginSessionId,
                ssoExtAuthComplete,
            },
            extSp,
            extLoginSessionId,
        });
        prevSkipExtPollingRef.current = skipExtPolling;
    }

    if (!hasLoggedInitialSnapshotRef.current) {
        hasLoggedInitialSnapshotRef.current = true;

        const hasSP = !!qsParsed.SP;
        const hasLoginSessionID = !!qsParsed.LoginSessionID;
        const hasSessionStorageBackup = !!ssoExtAuthParamsRef.current?.LoginSessionID;
        if (hasSP && !hasLoginSessionID && !hasSessionStorageBackup) {
            logLoginDebug('SSO ANOMALY: SP present but LoginSessionID missing from both URL and sessionStorage', {
                qsParsed,
                rawSearch: window.location.search,
                rawHref: window.location.href,
                referrer: document.referrer,
                ssoExtAuthParamsRefCurrent: ssoExtAuthParamsRef.current,
            });
        }
        if (!hasSP && !hasLoginSessionID && document.referrer && !document.referrer.includes(window.location.hostname)) {
            logLoginDebug('SSO ANOMALY: Login page loaded from external referrer but no SSO params in URL', {
                referrer: document.referrer,
                rawSearch: window.location.search,
                rawHref: window.location.href,
                hasSessionStorageBackup,
            });
        }

        logLoginDebug('View initialized with initial state snapshot', {
            qsParsed,
            extSp,
            extLoginSessionId,
            showError,
            showUpdateEmployeeIdLoginInfo,
            LocalizationLabelsArrayLength: LocalizationLabelsArray?.length,
            Email,           
            flashMsg,
            ShowPassword,
            SignupOption,
            SsoCompany,
            SsoOption,
            SamlLoginEndpoint,
            SamlLogoutEndpoint,
            LoginSystem,
            formValid,
            formErrors,
            shippingAddress,
            phone,
            firstName,
            isExtLoginError,
            skipPolling,
            showErrorForEmpIdLogin,
            authenticationDone,
            isSsoEmployeeLoginInfoLoaded,
            isToggled,
            isSwitching,
            isLoadingSiteInfo,
            rememberMe,
            flashMessage,
            user,
            hasObservedExtAuthRequest: hasObservedExtAuthRequestRef.current,
            loginExtDataRefCurrent: loginExtDataRef.current
        });
    }
    // Must use ref-backed ext* args + skip: extauth keeps polling after LoginSessionID is stripped from the URL (PII / LogRocket cleanup).
    const { data: loginExtData, error: loginExtError, isFetching: isExtLoading, isSuccess: isLoginExtSuccess, isError: isLoginExtError, isUninitialized } = useLoginExtQuery({
        externalSP: extSp,
        externalLoginSessionID: extLoginSessionId,
        isToggled: isToggled
    }, {
        skip: skipExtPolling,
        pollingInterval: 2000
    });

    useEffect(() => {
        if (!extLoginSessionId || skipExtPolling || pollStartedLoggedRef.current) return;
        pollStartedLoggedRef.current = true;
        logLoginDebug('sso_poll_started', {
            extSp,
            extLoginSessionId,
            skipPolling,
            skipExtPolling,
            pollAttempt: pollAttemptRef.current
        });
    }, [extSp, extLoginSessionId, skipPolling, skipExtPolling]);

    useEffect(() => {
        if (!extLoginSessionId || skipExtPolling) {
            prevIsExtLoadingRef.current = isExtLoading;
            return;
        }
        if (isExtLoading && !prevIsExtLoadingRef.current) {
            pollAttemptRef.current += 1;
            logLoginDebug('sso_poll_tick', {
                extSp,
                extLoginSessionId,
                skipPolling,
                skipExtPolling,
                pollAttempt: pollAttemptRef.current
            });
        }
        prevIsExtLoadingRef.current = isExtLoading;
    }, [isExtLoading, extSp, extLoginSessionId, skipPolling, skipExtPolling]);

    useEffect(() => {
        logLoginDebug('SSO polling hook state update', {
            extSp,
            extLoginSessionId,
            isToggled,
            skipPolling,
            skipExtPolling,
            hasLoginExtDataRefUserId: !!loginExtDataRef.current?.UserID,
            hasObservedExtAuthRequest: hasObservedExtAuthRequestRef.current,
            isExtLoading,
            isLoginExtSuccess,
            isLoginExtError,
            isUninitialized,
            loginExtError,
            loginExtData
        });
    }, [extSp, extLoginSessionId, isToggled, skipPolling, skipExtPolling, isExtLoading, isLoginExtSuccess, isLoginExtError, isUninitialized, loginExtError, loginExtData]);

    useEffect(() => {
        if (!extLoginSessionId || !isUninitialized) return;
        const timer = setTimeout(() => {
            logLoginDebug('SSO ANOMALY: useLoginExtQuery still uninitialized 3s after mount with valid LoginSessionID', {
                extSp,
                extLoginSessionId,
                skipPolling,
                skipExtPolling,
                isUninitialized,
                hasObservedExtAuthRequest: hasObservedExtAuthRequestRef.current,
                loginExtDataRefHasUserID: !!loginExtDataRef.current?.UserID,
            });
        }, 3000);
        return () => clearTimeout(timer);
    }, [extLoginSessionId, isUninitialized, extSp, skipPolling, skipExtPolling]);

    useEffect(() => {
        if (!isLoginExtError) return;
        logLoginDebug('SSO polling error observed', {
            extSp,
            extLoginSessionId,
            skipPolling,
            skipExtPolling,
            loginExtError,
            isExtLoading,
            isLoginExtSuccess,
            isLoginExtError,
            pollAttempt: pollAttemptRef.current
        });
        logBackendUnreachable('useLoginExtQuery', loginExtError, {
            externalSP: extSp,
            externalLoginSessionID: extLoginSessionId,
            skipPolling,
            skipExtPolling,
            isToggled,
            pollAttempt: pollAttemptRef.current
        });
    }, [isLoginExtError, loginExtError, extSp, extLoginSessionId, skipPolling, skipExtPolling, isToggled, isExtLoading, isLoginExtSuccess]);

    useEffect(() => {
        if (!ssoExtAuthParamsRef.current?.LoginSessionID) return;
        if (!hasObservedExtAuthRequestRef.current) return;
        const params = new URLSearchParams(window.location.search);
        if (!params.has('LoginSessionID')) return;
        params.delete('LoginSessionID');
        const search = params.toString();
        logLoginDebug('Removing LoginSessionID from URL after ext auth request observed', {
            currentSearch: window.location.search,
            nextSearch: search,
            path: window.location.pathname
        });
        navigate({ pathname: window.location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    }, [navigate, isExtLoading, isLoginExtSuccess, isLoginExtError]);

    useEffect(() => {
        if (!extLoginSessionId) return;
        if (isExtLoading || isLoginExtSuccess || isLoginExtError) {
            hasObservedExtAuthRequestRef.current = true;
            logLoginDebug('Marked ext auth request as observed', {
                extLoginSessionId,
                isExtLoading,
                isLoginExtSuccess,
                isLoginExtError,
                hasObservedExtAuthRequest: hasObservedExtAuthRequestRef.current
            });
        }
    }, [extLoginSessionId, isExtLoading, isLoginExtSuccess, isLoginExtError]);

    useEffect(() => {
        if (!extLoginSessionId) return;
        if (!(skipPolling || authenticationDone === true || qsParsed.ExtLoginError)) return;
        try {
            logLoginDebug('Clearing persisted SSO params from sessionStorage', {
                extLoginSessionId,
                skipPolling,
                authenticationDone,
                extLoginError: qsParsed.ExtLoginError,
                key: SSO_EXT_AUTH_SESSION_KEY
            });
            sessionStorage.removeItem(SSO_EXT_AUTH_SESSION_KEY);
        } catch (error) {
            console.log(error);
            logLoginDebug('Failed clearing persisted SSO params from sessionStorage', {
                key: SSO_EXT_AUTH_SESSION_KEY,
                error
            });
        }
    }, [extLoginSessionId, skipPolling, authenticationDone, qsParsed.ExtLoginError]);

    const removeFormError = (e) => {
        delete formErrors[e];
        setFormErrors({ ...formErrors });
    };

    const employeeIdLogin = async (employeeId, password, isExternalLogin) => {
        logLoginDebug('employeeIdLogin invoked', {
            employeeId,
            passwordLength: password?.length,
            isExternalLogin,
            isSsoEmployeeLoginInfoLoaded
        });
        if (loginInFlightRef.current) {
            logLoginDebug('employeeIdLogin skipped; login already in flight', {
                employeeId,
                isExternalLogin
            });
            return;
        }
        if (isSsoEmployeeLoginInfoLoaded === true) {
            logLoginDebug('employeeIdLogin aborted; SSO employee login info already loaded', {
                employeeId,
                isExternalLogin
            });
            return;
        }
        loginInFlightRef.current = true;
        let handedToSubmitLogin = false;
        try {
        setIsSsoEmployeeLoginInfoLoaded(true);
        dispatch(rootAction.commonAction.setLoading(true));
        const headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        };
        const param = {
            requestParameters: {
                employeeId: employeeId,
                password: password,
                isExternalLogin: isExternalLogin
            }
        };
        logLoginDebug('Calling GET_EMPLOYEE_ID_LOGIN_INFO', {
            endpoint: Config.END_POINT_NAMES['GET_EMPLOYEE_ID_LOGIN_INFO'],
            headers,
            param
        });
        var response = await MakeApiCallAsync(Config.END_POINT_NAMES['GET_EMPLOYEE_ID_LOGIN_INFO'], null, param, headers, "POST", true);
        logLoginDebug('GET_EMPLOYEE_ID_LOGIN_INFO response received', {
            response
        });
        dispatch(rootAction.commonAction.setLoading(false));
        let parsed = JSON.parse(response.data.data);
        logLoginDebug('Parsed employeeIdLogin response payload', {
            parsed
        });
        if (parsed.InvalidCredentials) {
            logLoginDebug('employeeIdLogin InvalidCredentials branch', {
                employeeId,
                parsed
            });
            setShowError(true);
            setShowErrorForEmpIdLogin(true);
        }
        else {
            if (parsed.RequiresEmailUpdate) {
                logLoginDebug('employeeIdLogin RequiresEmailUpdate branch', {
                    employeeId,
                    parsed
                });
                setPhone(parsed.Phone);
                setFirstName(parsed.FirstName);
                setShippingAddress({
                    address1: parsed.ShippingAddress?.address1, 
                    address2: parsed.ShippingAddress?.address2, 
                    city: parsed.ShippingAddress?.city, 
                    stateCode: parsed.ShippingAddress?.state_code, 
                    countryCode: "US", 
                    postalCode: parsed.ShippingAddress?.postal_code,
                    allotmentAddressLabel: parsed.ShippingAddress?.allotmentAddressLabel,
                    allotmentAddressId: parsed.ShippingAddress?.allotmentAddressId
                });
                setShowUpdateEmployeeIdLoginInfo(true);  
            } else {
                logLoginDebug('employeeIdLogin proceeding to submitLogin', {
                    employeeId,
                    parsed
                });
                handedToSubmitLogin = true;
                loginInFlightRef.current = false;
                submitLogin();
            }
        }
        } finally {
            if (!handedToSubmitLogin) {
                loginInFlightRef.current = false;
            }
        }
    };

    const submitLogin = () => {
        if (loginInFlightRef.current || isFetching || isLoading) {
            logLoginDebug('submitLogin skipped; login already in flight', {
                Email,
                isFetching,
                isLoading
            });
            return;
        }
        loginInFlightRef.current = true;
        logLoginDebug('submitLogin invoked', {
            Email,
            PasswordLength: Password?.length,
            LoginSystem,
            isToggled
        });
        login({ Email, Password })
        .unwrap()
            .then(({UserID, JWTToken, RequiresEmailUpdate, AllotmentPerepectiveEnabled, SizingPortalEnabled, SizingTemplate}) => {
                logLoginDebug('submitLogin success payload received', {
                    UserID,
                    hasJWTToken: !!JWTToken,
                    RequiresEmailUpdate,
                    AllotmentPerepectiveEnabled,
                    SizingPortalEnabled,
                    SizingTemplate,
                    isToggled
                });
                if (UserID && JWTToken && !RequiresEmailUpdate) {
                    logTerminalEventOnce('login_success', {
                        source: 'submitLogin',
                        UserID,
                        pollAttempt: pollAttemptRef.current
                    });
                    if(AllotmentPerepectiveEnabled && AllotmentPerepectiveEnabled==="true" && (SizingPortalEnabled || SizingTemplate?.HasAllotmentReturns || SizingTemplate?.HasAdditionalAllotment)){
                        if(!isToggled){
                            logLoginDebug('Routing decision: submitLogin -> switchToOtherPerspective (allotment perspective enabled, currently not toggled)', {
                                UserID,
                                isToggled,
                                AllotmentPerepectiveEnabled,
                                SizingPortalEnabled,
                                SizingTemplate
                            });
                            switchToOtherPerspective();
                        } else {
                            logLoginDebug('Routing decision: submitLogin -> navigate("/") (allotment perspective enabled, already toggled)', {
                                UserID,
                                isToggled
                            });
                            navigate('/');
                        }
                    }else{
                        if(isToggled){
                            logLoginDebug('Routing decision: submitLogin -> switchToOtherPerspective (allotment perspective disabled, currently toggled)', {
                                UserID,
                                isToggled,
                                AllotmentPerepectiveEnabled,
                                SizingPortalEnabled,
                                SizingTemplate
                            });
                            switchToOtherPerspective();
                        }else{
                            logLoginDebug('Routing decision: submitLogin -> navigate("/")', {
                                UserID,
                                isToggled
                            });
                            navigate('/');
                        }
                    }
                    setAuthenticationDone(true);
                    showLiveChat();
                }
            }).catch((error) => {
            logLoginDebug('submitLogin failed (catch branch)', {
                Email,
                LoginSystem,
                isToggled,
                error
            });
            logBackendUnreachable('submitLogin', error, {
                Email,
                LoginSystem,
                isToggled
            });
            logTerminalEventOnce('login_failed', {
                source: 'submitLogin',
                Email,
                LoginSystem,
                pollAttempt: pollAttemptRef.current,
                error
            });
            setShowError(true);
            setShowErrorForEmpIdLogin(true);
        })
        .finally(() => {
            loginInFlightRef.current = false;
            logLoginDebug('submitLogin finally branch', {
                formValid,
                showError,
                showErrorForEmpIdLogin
            });
            setFormValid(false);
        });
    };

    const switchToOtherPerspective = async() => {
        logLoginDebug('switchToOtherPerspective invoked', {
            isToggled,
            user
        });
        setIntentToSwitch(false);
        const Email = "NOOP";
        const Password = "NOOP";
        showSwitching();
        const currIsToggled = !isToggled;
        logLoginDebug('switchToOtherPerspective toggling perspective', {
            previousIsToggled: isToggled,
            nextIsToggled: currIsToggled
        });
        localStorage.setItem('toggleSizing', JSON.stringify(currIsToggled));
        setIsToggled(currIsToggled);
        //Refresh SiteConfig
        await getSiteInfo()
            .unwrap()
            .then((siteInfoResponse) => {
                logLoginDebug('switchToOtherPerspective getSiteInfo success', {
                    siteInfoResponse
                });
            })
            .catch((error) => {
                console.log(error);
                logLoginDebug('switchToOtherPerspective getSiteInfo failed', {
                    error
                });
                logBackendUnreachable('switchToOtherPerspective.getSiteInfo', error, {
                    endpoint: Config.END_POINT_NAMES.SITE_CONFIG,
                    isToggled
                });
            });
        switchSession({ Email, Password })
        .unwrap()
        .then((switchSessionResponse) => {
            logLoginDebug('switchToOtherPerspective switchSession success', {
                switchSessionResponse
            });
        })
        .catch((error) => {
            logLoginDebug('switchToOtherPerspective switchSession failed; reverting toggle', {
                error,
                currentIsToggled: isToggled
            });
            logBackendUnreachable('switchToOtherPerspective.switchSession', error, {
                currentIsToggled: isToggled
            });
            setIsToggled(!isToggled);            
        })
        .finally(() => {
            logLoginDebug('Routing decision: switchToOtherPerspective -> navigate("/")', {
                user
            });
            navigate('/');
            hideSwitching();
        });
    }

    // what login page to show
    useEffect(() => {
        logLoginDebug('formValid effect triggered', {
            formValid,
            LoginSystem,
            Email,
            PasswordLength: Password?.length
        });
        if (formValid) {
            if (LoginSystem == 1) {
                logLoginDebug('formValid routing decision: LoginSystem == 1 -> submitLogin', {
                    LoginSystem,
                    formValid
                });
                submitLogin();
            }
            if (LoginSystem == 2) {
                logLoginDebug('formValid routing decision: LoginSystem == 2 -> employeeIdLogin', {
                    LoginSystem,
                    formValid,
                    Email
                });
                setIsSsoEmployeeLoginInfoLoaded(false);
                employeeIdLogin(Email, Password, false);
            }
        }
        return () => {
            logLoginDebug('formValid effect cleanup resetting formValid', {
                previousFormValid: formValid
            });
            setFormValid(null);
        }
    }, [formValid]);

    // Set Flash Message
    useEffect(() => {
        logLoginDebug('user effect triggered', {
            user,
            rememberMe,
            flashMessage,
            authenticationDone
        });
        if (flashMessage) {
            setFlashMsg(flashMessage)
            dispatch(rootAction.userAction.resetFlashMessage());
        }
        if (user?.EmailAddress && rememberMe) {
            if (rememberMe) {
                setEmail(user.EmailAddress)
            }
        }
        if (user?.UserID && user?.JWTToken && !user?.RequiresEmailUpdate && authenticationDone === null) {
            logLoginDebug('user effect setAuthenticationDone(true)', {
                user,
                authenticationDone
            });
            setAuthenticationDone(true);
        } else {
            logLoginDebug('user effect setAuthenticationDone(false)', {
                user,
                authenticationDone
            });
            setAuthenticationDone(false);
        }
    }, [user]);

    // Reset PLP, cart and checkout
    useEffect(() => {
        dispatch(rootAction.plpActions.reset());
        dispatch(rootAction.cartActions.reset());
        dispatch(rootAction.checkoutActions.reset({ force: true }));
    }, [dispatch]);

    // SSO Poller — time-based timeout (use extLoginSessionId so timeout still applies after URL cleanup)
    useEffect(() => {
        if (!extLoginSessionId || skipPolling) return;
        logLoginDebug('SSO poll timeout timer started', {
            extLoginSessionId,
            skipPolling,
            timeoutMs: 30000,
            pollAttempt: pollAttemptRef.current
        });
        const timer = setTimeout(() => {
            logLoginDebug('SSO poll timeout reached; setting skipPolling=true', {
                extLoginSessionId,
                timeoutMs: 30000,
                pollAttempt: pollAttemptRef.current
            });
            logTerminalEventOnce('sso_poll_timeout', {
                extSp,
                extLoginSessionId,
                timeoutMs: 30000,
                pollAttempt: pollAttemptRef.current
            });
            setSkipPolling(true);
        }, 30000);
        return () => {
            logLoginDebug('SSO poll timeout timer cleared', {
                extLoginSessionId,
                skipPolling
            });
            clearTimeout(timer);
        };
    }, [extLoginSessionId, skipPolling]);

    // SSO Login — only when SP/LoginSessionID flow is active. Without this guard, cached extauth data
    // could still run success/navigation while useLoginExtQuery is skipped (non-SSO / password login).
    useEffect(() => {
        if (!extLoginSessionId) {
            return;
        }
        logLoginDebug('loginExtData effect triggered (poll result processing)', {
            loginExtData,
            isExtLoading,
            isLoginExtSuccess,
            isLoginExtError,
            skipPolling,
            extSp,
            extLoginSessionId,
            authenticationDone,
            isToggled
        });
        const pollCustomerNumber = loginExtData?.CustomerNo || loginExtData?.customerNo || loginExtData?.customer_no || null;
        if (pollCustomerNumber) {
            logLoginDebug('SSO poll returned customer number', {
                customerNumber: pollCustomerNumber,
                loginExtData
            });
        }
        if (loginExtData?.UserID) {
            loginExtDataRef.current = loginExtData;
            logLoginDebug('Stored loginExtData into ref', {
                loginExtDataRefCurrent: loginExtDataRef.current
            });
        }
        if(loginExtData && loginExtData.UserID && loginExtData.UserID.length>5 && loginExtData.JWTToken && loginExtData.JWTToken.length>5){
            logLoginDebug('sso_poll_success', {
                extSp,
                extLoginSessionId,
                pollAttempt: pollAttemptRef.current,
                loginExtData
            });
            logTerminalEventOnce('sso_login_success', {
                source: 'useLoginExtQuery',
                extSp,
                extLoginSessionId,
                pollAttempt: pollAttemptRef.current,
                loginExtData
            });
            setSsoExtAuthComplete(true);
            setAuthenticationDone(true);
            console.log('logged in', {allPerEnabled: loginExtData.AllotmentPerepectiveEnabled, sizePorEnabled: loginExtData.SizingPortalEnabled, sizeTemplate: loginExtData.SizingTemplate })
            if(loginExtData.AllotmentPerepectiveEnabled && loginExtData.AllotmentPerepectiveEnabled==="true" && (loginExtData.SizingPortalEnabled || loginExtData.SizingTemplate?.HasAllotmentReturns ||loginExtData.SizingTemplate?.HasAdditionalAllotment)){
                if(!isToggled){
                    logLoginDebug('Routing decision: loginExtData success -> switchToOtherPerspective (allotment perspective enabled, currently not toggled)', {
                        loginExtData,
                        isToggled
                    });
                    switchToOtherPerspective();
                } else {
                    logLoginDebug('Routing decision: loginExtData success -> navigate("/") (allotment perspective enabled, already toggled)', {
                        loginExtData,
                        isToggled
                    });
                    navigate('/');
                }
            }else{
                if(isToggled){
                    logLoginDebug('Routing decision: loginExtData success -> switchToOtherPerspective (allotment perspective disabled, currently toggled)', {
                        loginExtData,
                        isToggled
                    });
                    switchToOtherPerspective();
                }else{
                    logLoginDebug('Routing decision: loginExtData success -> navigate("/")', {
                        loginExtData,
                        isToggled
                    });
                    navigate('/');
                }                
            }
        } else {
            logLoginDebug('loginExtData did not include valid auth payload', {
                loginExtData,
                isLoginExtError,
                isLoginExtSuccess,
                isExtLoading
            });
            setAuthenticationDone(false);
            if (loginExtData && loginExtData.RequiresEmailUpdate) {
                logLoginDebug('loginExtData RequiresEmailUpdate branch -> employeeIdLogin', {
                    employeeId: loginExtData.EmployeeID,
                    loginExtData
                });
                setSsoExtAuthComplete(true);
                employeeIdLogin(loginExtData.EmployeeID, '', true);        
            }
        }
        // extLoginSessionId: re-run when SSO session appears; loginExtData: poll results. Intentionally not listing isToggled/navigate to avoid duplicate navigations.
    }, [loginExtData, extLoginSessionId]);

    const submitLoginForm = (event) => {
        event.preventDefault();
        if (loginInFlightRef.current || isFetching || isLoading) {
            logLoginDebug('submitLoginForm skipped; login already in flight', {
                isFetching,
                isLoading
            });
            return;
        }
        let errors = {}
        resetFlash()
        logLoginDebug('submitLoginForm invoked', {
            Email,
            PasswordLength: Password?.length,
            LoginSystem,
            showError,
            showErrorForEmpIdLogin
        });

        // Password Validation
        const regEx_Uppercase = new RegExp(/.*[A-Z]/);
        const regEx_Number = new RegExp(/.*\d/);
        const regEx_Length = new RegExp(/.{8,}$/);
        const regEx_SpecialChars = new RegExp(/.*[-’/`~!#*$@_%+=.,^&(){}[\]|;:”<>?\\]/);

        const regEx_PasswordValid = new RegExp(`^(?=${[
            regEx_Length.source,
            regEx_Uppercase.source,
            regEx_Number.source,
            regEx_SpecialChars.source
        ].join(")(?=")}).*$`);

        if (LoginSystem === 1) {
            if (!Email || Email === '') errors.Email = 'Please enter your Email'
            else if (Email && !validateEmail(Email)) errors.Email = 'Please enter a valid Email address'
        } else {
            if (!Email || Email === '') errors.Email = 'Please enter your Employee ID'
        }

        if (!Password || Password === '') errors.Password = 'Please enter your Password'
        else if (regEx_PasswordValid.test(Password) === false) errors.Password = 'Sorry, the provided password does not match the required constraints.'

        setFormErrors(errors);
        logLoginDebug('submitLoginForm validation results', {
            errors,
            hasValidationErrors: Object.keys(errors).length > 0
        });

        if (Object.keys(errors).length > 0) {
            setFormValid(false);
        } else {
            setFormValid(true);
        }
    }

    var resetFlash = () => {
        setFlashMsg('')
        dispatch(rootAction.userAction.resetFlashMessage());
    }

    useEffect(() => {
        // declare the data fetching function
        const dataOperationFunc = async () => {
            logLoginDebug('Fetching localization controls for Login screen', {
                entity: GlobalEnums.Entities["Login"]
            });
            //-- Get website localization data
            let arryRespLocalization = await GetLocalizationControlsJsonDataForScreen(GlobalEnums.Entities["Login"], null);
            logLoginDebug('Localization controls response received', {
                arryRespLocalizationLength: arryRespLocalization?.length,
                arryRespLocalization
            });
            if (arryRespLocalization != null && arryRespLocalization != undefined && arryRespLocalization.length > 0) {
                setLocalizationLabelsArray(arryRespLocalization);
            }
        }
        // call the function
        dataOperationFunc().catch(console.error);

        if(qsParsed.ExtLoginError){
            logLoginDebug('ExtLoginError found in query string', {
                extLoginError: qsParsed.ExtLoginError,
                qsParsed
            });
            setFlashMsg(qsParsed.ExtLoginError);
            setIsExtLoginError(true);
        }

        return () => {
            logLoginDebug('Login initialization effect cleanup', {
                qsParsed,
                showError
            });
            setShowError(false);
        }
    }, [qsParsed]);

    if (authenticationDone && user?.UserID && user?.JWTToken) {
        logLoginDebug('Render-level routing decision: already authenticated user -> navigate("/")', {
            authenticationDone,
            user
        });
        navigate('/');
    }
    
    if (showUpdateEmployeeIdLoginInfo) {
        const isExternalLogin = SsoOption === true && qsParsed.showUaLogin === false;
        return (<UpdateEmployeeIdLoginInfo 
                    shippingAddress={shippingAddress}
                    phone={phone}
                    firstName={firstName}
                    password={Password}
                    employeeId={isExternalLogin ? loginExtData.EmployeeID : Email}
                    enableSso={SsoOption}
                />);
    }
    else if (authenticationDone === false)
    return (
        <>
            <LoadingScreen loading={isFetching || isLoading || isExtLoading || (!isLoginExtSuccess && !isUninitialized)} />
            <Seo title="Login" description="Login" keywords="Login" />

            {/* <SiteBreadcrumb title="Login" /> */}

            <section id="maincontent" className="login-area pt-6 pt-md-8 pb-9">
                <div className="container-fluid">
                    {showPunchOutOnlyMessage ? (
                        <>
                            <h2 className='account-page-title text-center mb-5'>
                                {LocalizationLabelsArray.length > 0 ?
                                    replaceLoclizationLabel(LocalizationLabelsArray, "Account Login", "lbl_login_accountlogin")
                                    :
                                    "Account Login"
                                }
                            </h2>
                            <div className="row">
                                <div className="col col-md-8 col-lg-6 mx-auto text-center">
                                    <p className="mb-3">
                                        <strong>This catalog is configured for PunchOut access only.</strong>
                                    </p>
                                    <p className="mb-3">
                                        Please launch the catalog from your organization&apos;s procurement system.
                                    </p>
                                    <p className="mb-0">
                                        If you believe you have reached this page in error, please contact your procurement administrator or UA Customer Support.
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                    <h2 className='account-page-title text-center mb-5'>
                        {LocalizationLabelsArray.length > 0 ?
                            replaceLoclizationLabel(LocalizationLabelsArray, "Account Login", "lbl_login_accountlogin")
                            :
                            "Account Login"
                        }
                    </h2>
                    {/* <p className='text-center mb-5'>No account, you can still track your order.</p> */}
                    <div className="row">
                        {SsoOption === true && qsParsed.showUaLogin === false ?
                            <div className="col col-md-6 mx-auto">
                                <SSOCustomerCard
                                    ssoLoginEp={SamlLoginEndpoint}
                                    ssoLogoutEp={SamlLogoutEndpoint}
                                    ssoCompany={SsoCompany}
                                    showExtError={skipPolling||isExtLoginError}
                                    loginCustomWording={accountLoginIntroText}
                                    ssoButtonText={ssoButtonText}
                                />
                            </div>
                            :
                            <div className={`${SignupOption ? 'col-12 col-md-6' : 'col-12 col-md-6 mx-auto'}`}>
                                <Card color="light" className="border-0 card--has-bg-color h-100">
                                    <CardHeader className='border-0 card-header--no-divisor'>
                                        {accountLoginIntroText?.length > 0 ?
                                            <>
                                            <div className="clearfix" dangerouslySetInnerHTML={{ __html: accountLoginIntroText }}></div>
                                            </>
                                            :
                                            <>
                                            <CardTitle tag="h2">
                                                {LocalizationLabelsArray.length > 0 ?
                                                    replaceLoclizationLabel(LocalizationLabelsArray, "Returning Customers", "lbl_login_title")
                                                    :
                                                    "Returning Customers"
                                                }
                                            </CardTitle>
                                            <CardSubtitle tag="p">
                                                {LocalizationLabelsArray.length > 0 ?
                                                    replaceLoclizationLabel(LocalizationLabelsArray, "Welcome back! Sign in below.", "lbl_login_welcomesubtitle")
                                                    :
                                                    "Welcome back! Sign in below."
                                                }
                                            </CardSubtitle>
                                            <CardSubtitle tag="p">
                                                {LocalizationLabelsArray.length > 0 ?
                                                    replaceLoclizationLabel(LocalizationLabelsArray, "First-time logging in? Click Forgot Password and check your email for the reset link.", "lbl_login_resetpasswordinstructions")
                                                    :
                                                    "First-time logging in? Click Forgot Password and check your email for the reset link."
                                                }
                                            </CardSubtitle>
                                            </>
                                        } 
                                    </CardHeader>
                                    <CardBody>
                                        <Form className="login-form d-flex flex-column h-100" onSubmit={submitLoginForm} noValidate>
                                            <Alert color="danger" className={`${showError || showErrorForEmpIdLogin ? 'd-block' : 'd-none'}`}>
                                                {LocalizationLabelsArray.length > 0 ?
                                                    replaceLoclizationLabel(LocalizationLabelsArray, "Oops! We don’t recognize the username or password you entered. Please try again and remember passwords are case sensitive.", "lbl_login_incorrectpasswordmessage")
                                                    :
                                                    "Oops! We don’t recognize the username or password you entered. Please try again and remember passwords are case sensitive."
                                                }
                                            </Alert>
                                            <Alert color="danger" className={`${!showError && flashMsg.length > 0 ? 'd-block' : 'd-none'}`}>
                                                {flashMsg}
                                            </Alert>

                                            <FormGroup className='required'>
                                                <Label>
                                                    {LoginSystem === 1 ?
                                                        LocalizationLabelsArray.length > 0 ?
                                                            replaceLoclizationLabel(LocalizationLabelsArray, "Email", "lbl_login_email")
                                                            :
                                                            "Email"
                                                        :
                                                        LocalizationLabelsArray.length > 0 ?
                                                            replaceLoclizationLabel(LocalizationLabelsArray, "Employee ID", "lbl_login_employeeid")
                                                            :
                                                            "Employee ID"
                                                    }
                                                </Label>
                                                <Input type={LoginSystem === 1 ? 'email' : 'text'} name='Email' id='Email' value={Email}
                                                    placeholder={LoginSystem === 1 ? 'Enter your email' : 'Enter your Employee ID'}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    onFocus={(e) => removeFormError(e.target.id)}
                                                    invalid={!!formErrors.Email}
                                                >
                                                </Input>
                                                <FormFeedback>{formErrors.Email}</FormFeedback>
                                            </FormGroup>

                                            <FormGroup className='required position-relative'>
                                                <Label>
                                                    {LocalizationLabelsArray.length > 0 ?
                                                        replaceLoclizationLabel(LocalizationLabelsArray, "Password", "lbl_login_password")
                                                        :
                                                        "Password"
                                                    }
                                                </Label>
                                                <Link to={''} className='btn-show-password btn-link position-absolute' onClick={(e) =>{e.preventDefault(); setShowPassword(!ShowPassword)}} tabIndex={-1}>
                                                    {ShowPassword ?
                                                        <>
                                                            {LocalizationLabelsArray.length > 0 ?
                                                                replaceLoclizationLabel(LocalizationLabelsArray, "Show", "lbl_login_show")
                                                                :
                                                                "Show"
                                                            }
                                                        </>
                                                        :
                                                        <>
                                                            {LocalizationLabelsArray.length > 0 ?
                                                                replaceLoclizationLabel(LocalizationLabelsArray, "Hide", "lbl_login_hide")
                                                                :
                                                                "Hide"
                                                            }
                                                        </>
                                                    }
                                                </Link>
                                                {ShowPassword ?
                                                    <Input type='password' name='Password' id='Password' value={Password} maxLength='30'
                                                        placeholder='Enter your password'
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        onFocus={(e) => removeFormError(e.target.id)}
                                                        invalid={!!formErrors.Password}
                                                    >
                                                    </Input>
                                                :
                                                    <Input type='text' name='Password' id='Password' value={Password} maxLength='30'
                                                        placeholder='Enter your password'
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        onFocus={(e) => removeFormError(e.target.id)}
                                                        invalid={!!formErrors.Password}
                                                    >
                                                    </Input>
                                                }
                                                <FormFeedback>{formErrors.Password}</FormFeedback>
                                            </FormGroup>
                                            <div className="d-flex justify-content-between">
                                                <FormGroup className='custom-control custom-checkbox'>
                                                    <Input id='RememberMe' className="custom-control-input" type='checkbox' name='RememberMe' checked={rememberMe} onChange={(e) => dispatch(rootAction.userAction.rememberMe(e.target.checked))} />
                                                    <Label for='RememberMe' className="custom-control-label" >
                                                        {LocalizationLabelsArray.length > 0 ?
                                                            replaceLoclizationLabel(LocalizationLabelsArray, "Remember Me", "lbl_login_rememberme")
                                                            :
                                                            "Remember Me"
                                                        }
                                                    </Label>
                                                </FormGroup>

                                                <Link to={`/${getLanguageCodeFromSession()}/reset-password`} className="forgot-password">
                                                    {LocalizationLabelsArray.length > 0 ?
                                                        replaceLoclizationLabel(LocalizationLabelsArray, "Forgot Password", "lbl_login_forgotpassword")
                                                        :
                                                        "Forgot Password"
                                                    }
                                                </Link>
                                            </div>
                                            <Button type='submit' color='primary' className='mt-auto' id="lbl_login_loginbtn" disabled={isFetching || isLoading}>
                                                {LocalizationLabelsArray.length > 0 ?
                                                    replaceLoclizationLabel(LocalizationLabelsArray, "Login", "lbl_login_loginbtn")
                                                    :
                                                    "Login"
                                                }
                                            </Button>
                                        </Form>
                                    </CardBody>
                                </Card>
                            </div>
                        }
                        {SignupOption === true ?
                            <div className="col-12 col-md-6 mt-5 mt-md-0">
                                <NewCustomerCard />
                            </div>
                            :
                            <></>
                        }
                    </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
    else
        return (
            <>
                <LoadingScreen loading={true} detailText='Loading...' />
            </>
        );
}

export default Login;
